import {
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { type FC, useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { useCreateCamera, useUpdateCamera } from '../../api/index.js';
import { type Camera } from '../../models/index.js';
import { Events, track } from '../../utils/index.js';
import FormDialog from '../Forms/FormDialog.js';
import { type CameraFormValues } from '../Forms/formTypes.js';
import { cameraFormSchema, updateCameraFormSchema } from '../Forms/validations.js';

const defaultValues: CameraFormValues = {
  name: '',
  ipAddress: '',
  port: '',
  username: '',
  password: '',
  vod: '',
  areaDescription: '',
  irDualCapture: false,
};

interface CameraFormProps {
  open: boolean;
  onClose: () => void;
  camera?: Camera;
  patientId?: string;
  refetch: () => void;
  patientName?: string;
}
const CameraForm: FC<CameraFormProps> = ({ open, camera, patientId, refetch, patientName, onClose }) => {
  const createMutation = useCreateCamera();
  const updateMutation = useUpdateCamera();

  const formattedData: CameraFormValues  = useMemo(() => {
    if (camera) {
      return {
        name: camera.name,
        ipAddress: camera.ipAddress,
        port: camera.port,
        username: camera.username,
        password: '',
        vod: camera.vod ?? '',
        areaDescription: camera.areaDescription ?? '',
        irDualCapture: camera.irDualCapture ?? false,
      };
    }
    return defaultValues;
  }, [camera]);

  const isAxis = !camera?.nvrId && !camera?.channel;

  return (
    <FormDialog
      open={open}
      name={'camera'}
      onClose={onClose}
      data={formattedData}
      schema={camera ? updateCameraFormSchema : cameraFormSchema}
      isEditing={!!camera}
      onSubmit={async (data, isEditing) => {
        if (isEditing && camera) {
          const { password, ...rest } = data;
          await updateMutation.mutateAsync({
            ...rest,
            ...(password && { password }),
            id: camera?.id,
            patientId,
          });
          track(Events.CameraUpdate, {
            cameraId: camera?.id,
            patientId,
          });
        } else if (patientId) {
          const response = await createMutation.mutateAsync({
            ...data,
            patientId,
          });
          track(Events.CameraCreation, {
            cameraId: response?.id,
            patientId,
          });
        }
      }}
      refetch={refetch}
    >
      {(control, errors) => (
        <>
        {patientName ? <Typography>Editing camera for patient: {patientName}</Typography> : null}
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Controller
            name={'name'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                InputLabelProps={{ required: true }}
                error={!!errors?.name}
                helperText={errors?.name?.message || ''}
                label={'Name'}
              />
            )}
          />
          <Controller
            name={'ipAddress'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                InputLabelProps={{ required: true }}
                error={!!errors?.ipAddress}
                helperText={errors?.ipAddress?.message || ''}
                label={'IP Address'}
              />
            )}
          />
          <Controller
            name={'port'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                type={'number'}
                sx={{
                  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                    display: 'none',
                  },
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                }}
                InputLabelProps={{ required: true }}
                error={!!errors?.port}
                helperText={errors?.port?.message || ''}
                label={'Port'}
              />
            )}
          />
          <Controller
            name={'username'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                InputLabelProps={{ required: true }}
                error={!!errors?.username}
                helperText={errors?.username?.message || ''}
                label={'Username'}
              />
            )}
          />
          <Controller
            name={'password'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                InputLabelProps={{ required: !camera }}
                error={!!errors?.password}
                type={'password'}
                helperText={
                  errors?.password?.message || (camera ? 'Leave blank to keep the current password.' : '')
                }
                label={'Password'}
              />
            )}
          />
          <Controller
            name={'vod'}
            control={control}
            render={({ field }) => <TextField {...field} label={'VOD ID'} />}
          />
          <Controller
            name={'areaDescription'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                multiline
                rows={3}
                error={!!errors?.areaDescription}
                helperText={
                  errors?.areaDescription?.message ||
                  'What this camera records (room, furniture, doors) — used as AI context.'
                }
                label={'Area description'}
              />
            )}
          />
          {isAxis ? (
            <Controller
              name={'irDualCapture'}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  label={'IR dual capture (record day + infrared for AI)'}
                />
              )}
            />
          ) : null}
        </Stack>
        </>
      )}
    </FormDialog>
  );
};

export default CameraForm;
